"use client";

import { Loader2, Pause, Play, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Area, Point } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SoundToggleButton } from "@/components/ui/media-sound-button";
import { Slider } from "@/components/ui/slider";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { getCroppedImgFromArea } from "@/lib/image-utils";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("components/image-upload/image-crop-modal");

export interface CropQueueItem {
  id: string;
  file: File;
  originalUrl: string;
  kind: "image" | "video";
}

export interface CroppedImageResult {
  id: string;
  kind: "image" | "video";
  croppedFile: File;
  croppedUrl?: string;
}

interface ImageCropModalProps {
  images: CropQueueItem[];
  aspect?: number;
  onComplete: (croppedImages: CroppedImageResult[]) => void;
  onCancel: (appliedImages: CroppedImageResult[]) => void;
  isOpen: boolean;
  layer?: number;
}

export function ImageCropModal({
  images,
  aspect = 1,
  onComplete,
  onCancel,
  isOpen,
  layer,
}: ImageCropModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [appliedCrops, setAppliedCrops] = useState<CroppedImageResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  // A large photo still has to decode after the dialog mounts; without this the
  // cropper is an unexplained black rectangle for the first few hundred ms.
  const [isMediaReady, setIsMediaReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const currentImage = images[currentIndex];
  const totalImages = images.length;
  const hasMore = currentIndex < totalImages - 1;
  const isVideo = currentImage?.kind === "video";

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
      setAppliedCrops([]);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setConfirmCancel(false);
      setProcessingError(null);
      setIsMediaReady(false);
    }
  }, [isOpen]);

  // Reset crop/zoom when switching images
  useEffect(() => {
    if (!currentImage) {
      return;
    }

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setIsVideoPlaying(false);
    setProcessingError(null);
    setIsMediaReady(false);
  }, [currentImage]);

  const toggleVideoPlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      try {
        await video.play();
        setIsVideoPlaying(true);
      } catch (error) {
        logger.warn("Could not play video preview", error);
      }
      return;
    }

    video.pause();
    setIsVideoPlaying(false);
  }, []);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleApply = async () => {
    if (!currentImage) return;
    if (currentImage.kind === "image" && !croppedAreaPixels) {
      return;
    }

    setIsProcessing(true);
    setProcessingError(null);
    try {
      videoRef.current?.pause();
      setIsVideoPlaying(false);

      const newCrop: CroppedImageResult =
        currentImage.kind === "video"
          ? {
              id: currentImage.id,
              kind: "video",
              croppedFile: currentImage.file,
            }
          : {
              id: currentImage.id,
              kind: "image",
              croppedFile: await getCroppedImgFromArea(
                currentImage.originalUrl,
                croppedAreaPixels as Area,
                currentImage.file.name,
              ),
            };

      if (newCrop.kind === "image") {
        newCrop.croppedUrl = URL.createObjectURL(newCrop.croppedFile);
      }

      const updatedAppliedCrops = [...appliedCrops, newCrop];
      setAppliedCrops(updatedAppliedCrops);

      if (hasMore) {
        // Move to next item in queue.
        setCurrentIndex((prev) => prev + 1);
      } else {
        // All items processed.
        onComplete(updatedAppliedCrops);
      }
    } catch (error) {
      // Never swallow this. A failed crop used to leave the button flashing
      // "Processing…" and settling back on "Done" with no message and no
      // progress — the viewer had no way to know anything had gone wrong, let
      // alone what to do about it.
      const message =
        error instanceof Error && error.message
          ? error.message
          : "This image could not be prepared. Try a different one.";

      logger.error("Error cropping:", {
        message,
        media_kind: currentImage.kind,
        file_type: currentImage.file.type,
        file_size_bytes: currentImage.file.size,
        crop_width: croppedAreaPixels?.width ?? null,
        crop_height: croppedAreaPixels?.height ?? null,
        error,
      });

      setProcessingError(message);
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  const requestCancel = () => {
    if (isProcessing || confirmCancel) return;
    setConfirmCancel(true);
    captureClientEvent(ANALYTICS_EVENTS.MEDIA_CROP_CANCEL_REQUESTED, {
      media_kind: currentImage.kind,
      media_count: totalImages,
      current_index: currentIndex,
      completed_crop_count: appliedCrops.length,
    });
  };

  const confirmDiscard = () => {
    captureClientEvent(ANALYTICS_EVENTS.MEDIA_CROP_CANCEL_CONFIRMED, {
      media_kind: currentImage.kind,
      media_count: totalImages,
      current_index: currentIndex,
      completed_crop_count: appliedCrops.length,
    });
    onCancel(appliedCrops);
  };

  const keepEditing = () => {
    setConfirmCancel(false);
    captureClientEvent(ANALYTICS_EVENTS.MEDIA_CROP_CANCEL_DISMISSED, {
      media_kind: currentImage.kind,
      media_count: totalImages,
      current_index: currentIndex,
    });
  };

  if (!currentImage) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          requestCancel();
        }
      }}
    >
      <DialogContent
        layer={layer}
        className="flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-[100dvw] flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:max-w-[900px] sm:rounded-2xl"
      >
        <DialogHeader className="shrink-0 border-b border-border px-4 pt-[max(env(safe-area-inset-top),0.75rem)] pb-3">
          <DialogTitle className="text-center text-base font-medium">
            {isVideo ? "Preview" : "Crop"}{" "}
            {totalImages > 1 ? `${currentIndex + 1} of ${totalImages}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review and crop selected media before it is uploaded.
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator - only show if multiple images */}
        {totalImages > 1 && (
          <div className="flex w-full gap-1.5 px-4 py-2">
            {images.map((img, i) => (
              <div
                key={img.id}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i < currentIndex
                    ? "bg-accent"
                    : i === currentIndex
                      ? "bg-accent"
                      : "bg-surface-alt"
                }`}
              />
            ))}
          </div>
        )}

        <div className="relative min-h-0 flex-1 bg-black">
          {!isMediaReady ? (
            <div
              aria-live="polite"
              className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black"
            >
              <Loader2
                className="h-6 w-6 animate-spin text-white/80"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-white/80">
                Loading {isVideo ? "video" : "photo"}…
              </p>
            </div>
          ) : null}

          {isVideo ? (
            <div className="flex h-full w-full items-center justify-center bg-black p-4">
              <div
                className="relative max-h-full w-[min(100%,420px)] overflow-hidden rounded-xl bg-black shadow-2xl ring-1 ring-white/15"
                style={{ aspectRatio: `${aspect} / 1` }}
              >
                <video
                  ref={videoRef}
                  src={currentImage.originalUrl}
                  className="h-full w-full object-cover"
                  playsInline
                  muted={isMuted}
                  preload="metadata"
                  // loadedmetadata, not loadeddata: with preload="metadata" a
                  // browser may never reach HAVE_CURRENT_DATA before playback,
                  // which would pin the loading overlay over the video.
                  onLoadedMetadata={() => setIsMediaReady(true)}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                >
                  <track kind="captions" />
                </video>
                <div className="pointer-events-none absolute inset-2 rounded-lg border border-dashed border-white/70" />
                <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
                  Video fills this frame
                </span>
                <button
                  type="button"
                  onClick={toggleVideoPlayback}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 transition hover:bg-black/20"
                  aria-label={
                    isVideoPlaying
                      ? "Pause video preview"
                      : "Play video preview"
                  }
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/65 text-white shadow-lg ring-1 ring-white/20 backdrop-blur">
                    {isVideoPlaying ? (
                      <Pause className="h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Play className="ml-0.5 h-6 w-6" aria-hidden="true" />
                    )}
                  </span>
                </button>
                <SoundToggleButton
                  muted={isMuted}
                  onToggle={() => setIsMuted((prev) => !prev)}
                  className="z-20"
                />
              </div>
            </div>
          ) : (
            <Cropper
              key={currentImage.id}
              image={currentImage.originalUrl}
              onMediaLoaded={() => setIsMediaReady(true)}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              showGrid={false}
              cropShape="rect"
              style={{
                containerStyle: {
                  backgroundColor: "#000",
                },
                cropAreaStyle: {
                  border: "none",
                  boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                },
              }}
            />
          )}
        </div>

        {/* Zoom Slider */}
        {!isVideo && (
          <div className="shrink-0 border-t border-border bg-background px-6 py-4">
            <div className="mx-auto flex max-w-sm items-center gap-4">
              <ZoomOut
                className="h-4 w-4 shrink-0 text-text-secondary"
                aria-hidden="true"
              />
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.01}
                onValueChange={(values: number[]) => setZoom(values[0])}
                className="flex-1"
              />
              <ZoomIn
                className="h-4 w-4 shrink-0 text-text-secondary"
                aria-hidden="true"
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="shrink-0 border-t border-border bg-background px-4 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {confirmCancel ? (
            <div
              className="rounded-xl border border-error/25 bg-error/6 p-3"
              role="alert"
            >
              <p className="text-sm font-semibold text-text-primary">
                Discard this {isVideo ? "video" : "image"}?
              </p>
              <p className="mt-0.5 text-xs text-text-secondary">
                {appliedCrops.length > 0
                  ? `${appliedCrops.length} completed item${
                      appliedCrops.length === 1 ? "" : "s"
                    } will still be uploaded; the current selection will be discarded.`
                  : "Your current crop will not be uploaded."}
              </p>
              <div className="mt-3 flex gap-3">
                <Button
                  type="button"
                  onClick={keepEditing}
                  className="flex-1 rounded-xl bg-[#635bff] py-5 font-semibold text-white hover:bg-[#544de8] dark:bg-[#635bff] dark:hover:bg-[#544de8]"
                >
                  Keep editing
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmDiscard}
                  className="flex-1 rounded-xl py-5 font-semibold"
                >
                  Discard
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {processingError ? (
                <div
                  className="rounded-xl border border-error/25 bg-error/6 p-3 text-sm text-text-primary"
                  role="alert"
                >
                  <p className="font-semibold">Could not use this media</p>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    {processingError} You can pick a different one, or skip it
                    with Cancel.
                  </p>
                </div>
              ) : null}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={requestCancel}
                  disabled={isProcessing}
                  className="flex-1 rounded-xl border-error/35 bg-transparent py-5 font-semibold text-error transition-all duration-200 hover:bg-error/8 dark:border-red-400/40 dark:text-red-300 dark:hover:bg-red-500/10"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleApply}
                  disabled={
                    isProcessing ||
                    (currentImage.kind === "image" && !croppedAreaPixels)
                  }
                  className="flex-1 rounded-xl bg-[#635bff] py-5 font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#544de8] dark:bg-[#635bff] dark:hover:bg-[#544de8]"
                >
                  {isProcessing
                    ? "Processing..."
                    : processingError
                      ? "Try again"
                      : hasMore
                        ? "Next"
                        : isVideo
                          ? "Use Video"
                          : "Done"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
